/**
 * frontend/src/pages/valuation/_signOffGateState.ts
 *
 * VALUATION-B3 sign-off — the gate the Finalize button and the sticky Next step
 * bar both read, and the one reason shown when it is closed. Pure: derived from
 * what ValuationSignOff.tsx has loaded, every render.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 */
import type { ValuationEngagement } from '@/types/valuation'
import type {
  PreflightResponse,
  RecomputeBody,
  SnapshotBody,
  TuningBody,
} from '@/types/valuationB3'
import { sameReason } from './_signOffRationale'

export function deriveSignOffGates({
  engagement,
  frozen,
  liveVersion,
  preflight,
  hasComputedValue,
  reissueDelta,
  tuningBody,
  draftTuning,
  rationale,
  canConclude,
}: {
  engagement: ValuationEngagement
  frozen: SnapshotBody | null
  liveVersion: number | null
  preflight: PreflightResponse | null
  hasComputedValue: boolean
  reissueDelta: PreflightResponse['reissue']
  tuningBody: RecomputeBody | null
  draftTuning: TuningBody | null
  rationale: string
  canConclude: boolean
}) {
  const concluded = engagement.value_kind === 'concluded'
  // Same rule as core/valuation/document_snapshot._check: only an owner-recorded, current,
  // calculated record can be rendered; a signer's re-finalize leaves the old files listed only.
  const ownerRecordedFinal = frozen !== null && frozen.signed_role === 'valuation_owner'
    && frozen.status === 'final' && !concluded
  const low = concluded ? engagement.concluded_value_low : engagement.calculated_value_low
  const high = concluded ? engagement.concluded_value_high : engagement.calculated_value_high
  // The engagement's PERSISTED band. Only Finalize writes this column pair
  // (VAL-INDUSTRY-WIRING-1 R2 step 1) — before the first Record/Finalize it
  // is always null. This is what the Deliverable card shows: what has
  // actually been recorded, not what could be.
  const hasBand = low !== null && high !== null

  // VAL-SIGNOFF-GATE-1: a conclusion of value still requires canConclude — that
  // fence does not widen (S5). A calculation engagement does not — a
  // valuation_owner may finalize (record) one without it; `willSign` below is
  // what tells the card whether THIS finalize also signs.
  // VAL-REISSUE-GUARD-1 R3: on a re-finalize, nothing-changed is itself a
  // reason not to allow the button — the disabled state in FinalizeCard
  // handles the message, this handles the actual gate.
  // Preflight is debounced 400ms: until it answers `reissue` is undefined
  // (null = nothing signed), and the button must not read as "unchanged".
  const reissueFingerprintPending = liveVersion !== null && !!tuningBody && preflight?.reissue === undefined
  // VAL-SIGNOFF-REASON-DISTINCT-1: the sign-off reason is the signer's own
  // words; the box opens empty and a copy of the Methodology reason is refused.
  const methodologyReason = draftTuning?.rationale ?? ''
  const repeatsMethodology = sameReason(rationale, methodologyReason)
  const needsMethodologyReason = !!preflight?.tuned && preflight.tuning_rationale_recorded === false
  // FIX_VAL-SIGNER-SIGN-1: a valuation signer can sign a value the owner recorded with no
  // input change. "Nothing has changed" applies only once a signer has signed the live version.
  const awaitingSignature = liveVersion !== null && canConclude && frozen !== null
    && frozen.signed_role !== 'valuation_signer'
  const gateOk = !!tuningBody && !!preflight?.ok && hasComputedValue
    && !needsMethodologyReason
    && (concluded ? canConclude : true)
    && (reissueDelta?.changed !== false || awaitingSignature)
    && !reissueFingerprintPending
  const canFinalize = gateOk && rationale.trim().length > 0 && !repeatsMethodology
  const blockedReason = concluded && !canConclude
    ? 'A conclusion of value may only be signed by a credentialed valuation role.'
    : !tuningBody
      ? 'Save your inputs on Intake and Methodology, then come back to Finalize.'
      : reissueFingerprintPending
        ? 'Checking for changes…'
        : !hasComputedValue
          ? 'The engine has not returned a computed value for these inputs yet.'
          : rationale.trim().length === 0
            ? 'Write the reason for this record.'
            : repeatsMethodology
              ? 'The sign-off reason must be your own words — it matches the Methodology reason.'
              : needsMethodologyReason
                ? 'Write the Methodology reason on Methodology first.'
                : preflight && !preflight.ok
                  ? `Blocked: ${preflight.blockers.join(' · ')}`
                  : null

  return {
    concluded, ownerRecordedFinal, low, high, hasBand,
    reissueFingerprintPending, methodologyReason, awaitingSignature, gateOk, canFinalize, blockedReason,
  }
}
